import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextareaAutosize,
  Stack,
  Divider,
  Paper,
  IconButton,
  Breadcrumbs,
  Link as MuiLink,
  CircularProgress,
  Alert,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  ArrowBack,
  Send,
  Business,
  Person,
  Email,
  Phone,
  CalendarToday,
  LocationOn,
  Group,
  Schedule,
  CheckCircle,
  Factory,
  Engineering,
  TrendingUp,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

interface BookTourProps {}

export const BookTour: React.FC<BookTourProps> = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    // Contact Information
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    companyName: '',
    jobTitle: '',
    
    // Tour Details
    tourDate: '',
    tourTime: '',
    groupSize: '',
    tourType: 'general',
    
    // Special Requirements
    specialInterests: '',
    accessibilityNeeds: '',
    additionalNotes: '',
    
    // Preferences
    preferredGuide: '',
    language: 'english',
  });

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // TODO: Implement actual API call
      // await tourApi.bookTour(formData);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      setSuccess(true);
    } catch (err) {
      setError('Failed to book tour. Please try again.');
      console.error('Error booking tour:', err);
    } finally {
      setLoading(false);
    }
  };

  const tourTypes = [
    {
      id: 'general',
      title: 'General Facility Tour',
      description: 'Overview of our manufacturing processes and capabilities',
      duration: '1-2 hours',
      maxGroupSize: 20,
    },
    {
      id: 'technical',
      title: 'Technical Deep Dive',
      description: 'Detailed technical tour for engineers and technical professionals',
      duration: '2-3 hours',
      maxGroupSize: 10,
    },
    {
      id: 'executive',
      title: 'Executive Briefing',
      description: 'High-level overview for executives and decision makers',
      duration: '1 hour',
      maxGroupSize: 8,
    },
    {
      id: 'student',
      title: 'Educational Tour',
      description: 'Educational tour for students and academic institutions',
      duration: '1.5 hours',
      maxGroupSize: 30,
    },
  ];

  const tourHighlights = [
    'State-of-the-art manufacturing equipment',
    'Quality control and testing facilities',
    'Automation and robotics systems',
    'Sustainable manufacturing practices',
    'Research and development labs',
    'Employee training facilities',
  ];

  const safetyGuidelines = [
    'Closed-toe shoes required',
    'Safety glasses and hard hats provided',
    'No photography in restricted areas',
    'Follow guide instructions at all times',
    'Stay within designated tour areas',
    'Report any safety concerns immediately',
  ];

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 3, color: 'white' }}>
          <MuiLink color="inherit" href="/" underline="hover">
            Home
          </MuiLink>
          <Typography color="inherit">Book a Tour</Typography>
        </Breadcrumbs>

        {/* Header */}
        <Paper
          elevation={0}
          sx={{
            p: 4,
            mb: 4,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: 3,
            border: '1px solid rgba(255, 255, 255, 0.2)',
          }}
        >
          <Typography variant="h3" gutterBottom sx={{ fontWeight: 700 }}>
            Book a Facility Tour
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Experience our state-of-the-art manufacturing facility firsthand. Schedule a guided tour to see our processes, meet our team, and learn about our capabilities.
          </Typography>
          
          <Stack direction="row" spacing={2} flexWrap="wrap">
            <Chip icon={<Schedule />} label="1-3 hours duration" />
            <Chip icon={<Group />} label="Up to 30 people" />
            <Chip icon={<LocationOn />} label="Nairobi, Kenya" />
            <Chip icon={<CheckCircle />} label="Free of charge" />
          </Stack>
        </Paper>

        <Grid container spacing={4}>
          {/* Tour Types */}
          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                mb: 4,
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                borderRadius: 3,
                border: '1px solid rgba(255, 255, 255, 0.2)',
              }}
            >
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                Tour Types
              </Typography>
              <Stack spacing={2}>
                {tourTypes.map((tour) => (
                  <Card
                    key={tour.id}
                    sx={{
                      cursor: 'pointer',
                      border: formData.tourType === tour.id ? '2px solid #6366f1' : '1px solid #e0e0e0',
                      background: formData.tourType === tour.id ? 'rgba(99, 102, 241, 0.05)' : 'transparent',
                    }}
                    onClick={() => handleInputChange('tourType', tour.id)}
                  >
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                        {tour.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {tour.description}
                      </Typography>
                      <Stack direction="row" spacing={2}>
                        <Chip label={tour.duration} size="small" />
                        <Chip label={`Max ${tour.maxGroupSize} people`} size="small" />
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </Paper>

            {/* Tour Highlights */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                mb: 4,
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                borderRadius: 3,
                border: '1px solid rgba(255, 255, 255, 0.2)',
              }}
            >
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                Tour Highlights
              </Typography>
              <List dense>
                {tourHighlights.map((highlight, index) => (
                  <ListItem key={index} sx={{ px: 0 }}>
                    <ListItemIcon>
                      <CheckCircle sx={{ color: 'primary.main', fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText primary={highlight} />
                  </ListItem>
                ))}
              </List>
            </Paper>

            {/* Safety Guidelines */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                borderRadius: 3,
                border: '1px solid rgba(255, 255, 255, 0.2)',
              }}
            >
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                Safety Guidelines
              </Typography>
              <List dense>
                {safetyGuidelines.map((guideline, index) => (
                  <ListItem key={index} sx={{ px: 0 }}>
                    <ListItemIcon>
                      <CheckCircle sx={{ color: 'warning.main', fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText primary={guideline} />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>

          {/* Booking Form */}
          <Grid item xs={12} md={8}>
            <Paper
              elevation={0}
              sx={{
                p: 4,
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                borderRadius: 3,
                border: '1px solid rgba(255, 255, 255, 0.2)',
              }}
            >
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                Tour Booking Form
              </Typography>
              <Divider sx={{ mb: 3 }} />

              <Grid container spacing={3}>
                {/* Contact Information */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    Contact Information
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Full Name"
                    value={formData.contactName}
                    onChange={(e) => handleInputChange('contactName', e.target.value)}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    type="email"
                    value={formData.contactEmail}
                    onChange={(e) => handleInputChange('contactEmail', e.target.value)}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    value={formData.contactPhone}
                    onChange={(e) => handleInputChange('contactPhone', e.target.value)}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Company Name"
                    value={formData.companyName}
                    onChange={(e) => handleInputChange('companyName', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Job Title"
                    value={formData.jobTitle}
                    onChange={(e) => handleInputChange('jobTitle', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Preferred Language</InputLabel>
                    <Select
                      value={formData.language}
                      onChange={(e) => handleInputChange('language', e.target.value)}
                      label="Preferred Language"
                    >
                      <MenuItem value="english">English</MenuItem>
                      <MenuItem value="swahili">Swahili</MenuItem>
                      <MenuItem value="french">French</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Tour Details */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
                    Tour Details
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Preferred Tour Date"
                    type="date"
                    value={formData.tourDate}
                    onChange={(e) => handleInputChange('tourDate', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Preferred Time"
                    type="time"
                    value={formData.tourTime}
                    onChange={(e) => handleInputChange('tourTime', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Number of People"
                    type="number"
                    value={formData.groupSize}
                    onChange={(e) => handleInputChange('groupSize', e.target.value)}
                    inputProps={{ min: 1, max: 30 }}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Preferred Guide (Optional)"
                    value={formData.preferredGuide}
                    onChange={(e) => handleInputChange('preferredGuide', e.target.value)}
                    placeholder="Guide name or special requirements"
                  />
                </Grid>

                {/* Special Requirements */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
                    Special Requirements
                  </Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Areas of Special Interest"
                    multiline
                    rows={3}
                    value={formData.specialInterests}
                    onChange={(e) => handleInputChange('specialInterests', e.target.value)}
                    placeholder="e.g., automation, quality control, sustainability, etc."
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Accessibility Needs"
                    multiline
                    rows={2}
                    value={formData.accessibilityNeeds}
                    onChange={(e) => handleInputChange('accessibilityNeeds', e.target.value)}
                    placeholder="Any accessibility requirements for tour participants"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Additional Notes"
                    multiline
                    rows={3}
                    value={formData.additionalNotes}
                    onChange={(e) => handleInputChange('additionalNotes', e.target.value)}
                    placeholder="Any other special requests or information"
                  />
                </Grid>

                {/* Submit Button */}
                <Grid item xs={12}>
                  <Box sx={{ mt: 3, textAlign: 'center' }}>
                    <Button
                      variant="contained"
                      size="large"
                      onClick={handleSubmit}
                      disabled={loading}
                      startIcon={loading ? <CircularProgress size={20} /> : <Send />}
                      sx={{
                        px: 6,
                        py: 1.5,
                        background: 'linear-gradient(135deg, #1b4d89 0%, #0f3460 100%)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #0f3460 0%, #1b4d89 100%)',
                        },
                      }}
                    >
                      {loading ? 'Booking Tour...' : 'Book Tour'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>

        {/* Success Message */}
        {success && (
          <Paper
            elevation={0}
            sx={{
              p: 4,
              mt: 4,
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              border: '1px solid rgba(255, 255, 255, 0.2)',
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
              <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
                Tour Booked Successfully!
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                Thank you for booking a tour. We'll confirm your appointment within 24 hours and send you detailed instructions.
              </Typography>
              <Stack direction="row" spacing={2} justifyContent="center">
                <Button
                  variant="contained"
                  onClick={() => navigate('/')}
                  sx={{
                    background: 'linear-gradient(135deg, #1b4d89 0%, #0f3460 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #0f3460 0%, #1b4d89 100%)',
                    },
                  }}
                >
                  Return Home
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/contact')}
                >
                  Contact Us
                </Button>
              </Stack>
            </Box>
          </Paper>
        )}

        {/* Error Message */}
        {error && (
          <Alert severity="error" sx={{ mt: 3 }}>
            {error}
          </Alert>
        )}
      </Container>
    </Box>
  );
}; 